import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { WafCaptchaService } from './waf-captcha.service';

/**
 * Attaches the AWS WAF token (`x-aws-waf-token`) to public intake API calls so they pass the
 * edge WAF challenge/CAPTCHA rule, and escalates to a visible inline CAPTCHA if WAF flags a
 * request. Scoped to the intake surface only — authenticated traffic is untouched.
 *
 * See .kiro/specs/waf-captcha-intake. No-ops when {@link WafCaptchaService} is unconfigured.
 */
@Injectable()
export class WafTokenInterceptor implements HttpInterceptor {
  /** Intake endpoints gated by the WAF rule (see the Terraform byte-match). */
  private static readonly INTAKE_PATTERNS = [
    '/api/uploads/intake/',
    '/api/intake-evictions/intake-form'
  ];

  /** In-flight escalation solve, shared so concurrent flagged requests reuse one overlay. */
  private pendingSolve: Promise<string> | null = null;

  constructor(private wafCaptcha: WafCaptchaService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.wafCaptcha.enabled || !this.isIntakeRequest(req.url)) {
      return next.handle(req);
    }

    return from(this.wafCaptcha.getToken()).pipe(
      switchMap((token) => next.handle(this.withToken(req, token))),
      catchError((error) => {
        if (this.isCaptchaChallenge(error)) {
          // WAF escalated this request to a visible puzzle — render it inline, then retry once.
          return from(this.solveCaptcha()).pipe(
            switchMap((token) => next.handle(this.withToken(req, token)))
          );
        }
        return throwError(() => error);
      })
    );
  }

  private isIntakeRequest(url: string): boolean {
    return WafTokenInterceptor.INTAKE_PATTERNS.some((p) => url.includes(p));
  }

  private withToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    return token ? req.clone({ setHeaders: { 'x-aws-waf-token': token } }) : req;
  }

  private isCaptchaChallenge(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 405 &&
      error.headers.get('x-amzn-waf-action') === 'captcha'
    );
  }

  /**
   * Render the inline WAF CAPTCHA in a lightweight modal overlay and resolve with the token.
   * Concurrent flagged requests share a single overlay/solve via {@link pendingSolve}.
   */
  private solveCaptcha(): Promise<string> {
    if (this.pendingSolve) {
      return this.pendingSolve;
    }

    // Remember what had focus so we can restore it after the modal closes (a11y).
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement('div');
    overlay.className = 'waf-captcha-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Security check');
    const container = document.createElement('div');
    container.tabIndex = -1;
    overlay.appendChild(container);
    document.body.appendChild(overlay);
    container.focus();

    this.pendingSolve = new Promise<string>((resolve, reject) => {
      // Keyboard a11y: Escape dismisses (so keyboard/SR users aren't trapped if the puzzle can't
      // be completed), and Tab is kept within the overlay (best-effort — the AWS widget renders in
      // an iframe, which captures its own focus, so this guards the surrounding page only).
      overlay.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          reject(new Error('WAF captcha dismissed'));
        } else if (e.key === 'Tab') {
          e.preventDefault();
          container.focus();
        }
      });
      this.wafCaptcha.renderCaptcha(container).then(resolve, reject);
    }).finally(() => {
      overlay.remove();
      previouslyFocused?.focus?.();
      this.pendingSolve = null;
    });
    return this.pendingSolve;
  }
}
